import { TourStep } from '../../types';

export const steps: TourStep[] = [
  // === INTRODUCTION ===
  {
    id: 'intro',
    type: 'modal',
    title: 'Bienvenue !',
    content: `Ce guide vous accompagnera dans la création d'une campagne de vote.

Vous apprendrez à :
• Créer une nouvelle campagne
• Définir le titre et les dates de vote
• Ajouter des questions au bulletin

💡 Vous pourrez entrer vos propres informations pour créer une vraie campagne.`,
    onEnter: [
      { type: 'navigate', path: '/admin/votez/campagnes' },
      { type: 'wait', delay: 500 },
    ],
  },

  // === NAVIGATION TO CREATION ===
  {
    id: 'click-new-campaign',
    type: 'highlight',
    target: 'new-campaign-btn',
    title: 'Créer une campagne',
    content: 'Cliquez sur ce bouton pour commencer la création d\'une nouvelle campagne.',
    position: 'bottom',
  },

  // === BASIC INFO ===
  {
    id: 'enter-title',
    type: 'highlight',
    target: 'campaign-title',
    title: 'Titre de la campagne',
    content: 'Le titre identifie votre campagne. Nous allons en créer une de démonstration.',
    position: 'right',
    onEnter: [
      { type: 'click', selector: '[data-showme="new-campaign-btn"]' },
      { type: 'wait', delay: 500 },
      { type: 'fill', selector: '[data-testid="titre-input"]', value: '[DÉMO] Ma première campagne' },
      { type: 'wait', delay: 300 },
    ],
  },

  // === DATES ===
  {
    id: 'configure-dates',
    type: 'highlight',
    target: 'voting-dates',
    title: 'Période de vote',
    content: 'Ces dates définissent quand le vote sera ouvert. Les dates par défaut conviennent pour ce guide.',
    position: 'top',
  },

  // === SAVE CAMPAIGN ===
  {
    id: 'save-campaign',
    type: 'modal',
    title: 'Enregistrement...',
    content: 'La campagne va être créée. Vous serez redirigé vers le tableau de bord.',
    onEnter: [
      { type: 'click', selector: '[data-testid="save-button"]' },
      { type: 'wait', delay: 2000 },
    ],
  },

  // === DASHBOARD: MAIN TABS ===
  {
    id: 'dashboard-tabs',
    type: 'highlight',
    target: 'main-tabs',
    title: 'Navigation principale',
    content: `Le tableau de bord est divisé en 3 onglets :
• Tableau de bord - Statistiques en temps réel
• Configuration - Paramètres de la campagne
• Résultats - Résultats une fois terminé`,
    position: 'bottom',
  },

  // === DASHBOARD: STATS CARDS ===
  {
    id: 'dashboard-stats-cards',
    type: 'highlight',
    target: 'stats-cards',
    title: 'Statistiques rapides',
    content: 'Ces cartes affichent les statistiques clés : temps restant, votes déposés, votants inscrits et poids total.',
    position: 'bottom',
  },

  // === DASHBOARD: PARTICIPATION ===
  {
    id: 'dashboard-participation',
    type: 'highlight',
    target: 'participation-gauge',
    title: 'Taux de participation',
    content: 'Ce graphique montre le taux de participation en temps réel. Il se met à jour automatiquement.',
    position: 'top',
  },

  // === GO TO CONFIGURATION ===
  {
    id: 'go-to-config',
    type: 'modal',
    title: 'Configuration',
    content: 'Allons maintenant dans l\'onglet Configuration pour ajouter des questions au bulletin.',
    onEnter: [
      { type: 'click', selector: '[data-testid="tab-configuration"]' },
      { type: 'wait', delay: 800 },
    ],
  },

  // === CONFIG SUB-TABS OVERVIEW ===
  {
    id: 'config-sub-tabs',
    type: 'highlight',
    target: 'config-sub-tabs',
    title: 'Sous-onglets de configuration',
    content: `La configuration est divisée en 4 sections. Explorons-les ensemble.`,
    position: 'bottom',
  },

  // === GENERAL TAB ===
  {
    id: 'config-general',
    type: 'highlight',
    target: 'config-general-content',
    title: 'Onglet Général',
    content: `L'onglet Général contient les paramètres essentiels :

📝 Titre et slug (URL) de la campagne
📅 Dates d'ouverture et fermeture du vote
🔒 Paramètres de sécurité (codes d'accès, tentatives)
📊 Options d'affichage des résultats
🎲 Ordre aléatoire des réponses`,
    position: 'right',
    onEnter: [
      { type: 'click', selector: '[data-testid="subtab-general"]' },
      { type: 'wait', delay: 500 },
    ],
  },

  // === APPARENCE TAB ===
  {
    id: 'config-apparence',
    type: 'highlight',
    target: 'config-apparence-content',
    title: 'Onglet Apparence',
    content: `L'onglet Apparence permet de personnaliser le look :

🎨 Choix du thème de couleurs
🖼️ Logo et image de fond
📄 Texte d'accueil personnalisé`,
    position: 'right',
    onEnter: [
      { type: 'click', selector: '[data-testid="subtab-apparence"]' },
      { type: 'wait', delay: 500 },
    ],
  },

  // === GO TO BULLETIN ===
  {
    id: 'go-to-bulletin',
    type: 'modal',
    title: 'Le Bulletin de Vote',
    content: `Passons maintenant à l'onglet Bulletin.

C'est ici que vous ajoutez les questions auxquelles les votants devront répondre.`,
    onEnter: [
      { type: 'click', selector: '[data-testid="subtab-bulletin"]' },
      { type: 'wait', delay: 800 },
    ],
  },

  // === QUESTION TYPES ===
  {
    id: 'question-types',
    type: 'highlight',
    target: 'question-add-button',
    title: 'Types de questions',
    content: `Ces boutons permettent d'ajouter 3 types de questions :

🗳️ Élection - Pour élire des candidats à un poste
✅ Proposition - Pour voter Oui/Non sur une résolution
📝 Choix libre - Pour choisir parmi des options personnalisées`,
    position: 'bottom',
  },

  // === DECISION: CHOOSE QUESTION TYPE ===
  {
    id: 'choose-question-type',
    type: 'decision',
    title: 'Quel type de question voulez-vous créer ?',
    content: 'Choisissez le type de question pour continuer le guide :',
    options: ['proposition', 'election'],
  },

  // ============================================
  // PROPOSITION BRANCH
  // ============================================

  // === ADD PROPOSITION ===
  {
    id: 'add-proposition',
    type: 'modal',
    title: 'Ajouter une proposition',
    content: 'Le formulaire de création de proposition va s\'ouvrir...',
    onEnter: [
      { type: 'click', selector: '[data-testid="question-add-proposition"]' },
      { type: 'wait', delay: 1000 },
      { type: 'fill', selector: '[data-testid="nom-input"]', value: 'Adoption du budget 2025' },
      { type: 'wait', delay: 300 },
      { type: 'fillHtml', value: '<p>Approuvez-vous le budget proposé pour l\'année 2025 ?</p>' },
      { type: 'wait', delay: 300 },
    ],
  },

  // === PROPOSITION FORM ===
  {
    id: 'proposition-form',
    type: 'modal',
    title: 'Formulaire de proposition',
    content: `Une proposition permet aux votants de répondre Oui ou Non.

Le formulaire permet de configurer :
• Nom de la question (affiché dans l'admin)
• Texte explicatif (affiché aux votants)
• Option d'abstention

Cliquez sur Suivant pour enregistrer cette question.`,
  },

  // === PROPOSITION CREATED ===
  {
    id: 'proposition-created',
    type: 'modal',
    title: 'Proposition créée ! ✅',
    content: `La proposition "Adoption du budget 2025" a été ajoutée au bulletin.

Les votants verront les options Oui et Non automatiquement.`,
    onEnter: [
      { type: 'click', selector: '[data-testid="save-button"]' },
      { type: 'wait', delay: 1500 },
    ],
  },

  // ============================================
  // ELECTION BRANCH
  // ============================================

  // === ADD ELECTION ===
  {
    id: 'add-election',
    type: 'modal',
    title: 'Ajouter une élection',
    content: 'Le formulaire de création d\'élection va s\'ouvrir...',
    onEnter: [
      { type: 'click', selector: '[data-testid="question-add-election"]' },
      { type: 'wait', delay: 1000 },
      { type: 'fill', selector: '[data-testid="nom-input"]', value: 'Élection du président' },
      { type: 'wait', delay: 300 },
      { type: 'fillHtml', value: '<p>Votez pour le prochain président du conseil.</p>' },
      { type: 'wait', delay: 300 },
    ],
  },

  // === ELECTION FORM ===
  {
    id: 'election-form',
    type: 'modal',
    title: 'Formulaire d\'élection',
    content: `Une élection permet de choisir parmi des candidats.

Le formulaire permet de configurer :
• Nom de la question (affiché dans l'admin)
• Texte explicatif (affiché aux votants)
• Min/Max de candidats à choisir
• Option d'abstention

Cliquez sur Suivant pour enregistrer cette question.`,
  },

  // === ELECTION CREATED ===
  {
    id: 'election-created',
    type: 'modal',
    title: 'Élection créée ! 🗳️',
    content: `La question "Élection du président" a été ajoutée au bulletin.

Note : Les candidats doivent être ajoutés séparément via le menu de la question.`,
    onEnter: [
      { type: 'click', selector: '[data-testid="save-button"]' },
      { type: 'wait', delay: 1500 },
    ],
  },

  // ============================================
  // COMMON ENDING
  // ============================================

  // === VOTANTS EXPLANATION ===
  {
    id: 'votants-explanation',
    type: 'modal',
    title: 'Configurer les votants',
    content: `Pour que les gens puissent voter, vous devez ajouter des votants.

Dans l'onglet "Votants", vous pouvez :
📤 Importer une liste Excel/CSV
➕ Ajouter des votants un par un
📧 Envoyer les codes d'accès par courriel`,
  },

  // === PUBLISH EXPLANATION ===
  {
    id: 'publish-explanation',
    type: 'modal',
    title: 'Publier la campagne',
    content: `Une fois prêt, publiez votre campagne :

1. Allez dans l'onglet "Général"
2. Cliquez sur "Actions" en haut à droite
3. Sélectionnez "Publier"

Le vote s'ouvrira à la date d'ouverture définie.`,
  },

  // === CONCLUSION ===
  {
    id: 'conclusion',
    type: 'modal',
    title: 'Bravo ! 🎉',
    content: `Vous avez créé votre campagne !

✅ Campagne créée avec titre et dates
✅ Question ajoutée au bulletin

Prochaines étapes :
• Importer les votants
• Publier quand tout est prêt

💡 Vous pouvez archiver cette campagne via le menu Actions si nécessaire.`,
  },
];
